import { useState } from 'react';
import { changePassword, EngineError } from '../../api/engineDashboard';

interface Props {
  token: string;
  onClose: () => void;
}

/** Self-service password change. The Engine rejects this during
 * impersonation, so the trigger is hidden while a stash exists. */
export function ChangePasswordDialog({ token, onClose }: Props) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const canSubmit = current.length > 0 && next.length >= 10 && next === confirm && !busy;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await changePassword(token, current, next);
      setDone(true);
    } catch (err) {
      setError(err instanceof EngineError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <form
        className="card w-full max-w-sm p-5"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) void submit();
        }}
      >
        <div className="text-sm font-semibold">Change password</div>
        {done ? (
          <>
            <p className="mt-3 text-sm text-gray-600">Your password has been updated.</p>
            <button type="button" className="btn-primary mt-4 w-full" onClick={onClose}>
              Done
            </button>
          </>
        ) : (
          <>
            <label className="mt-4 block">
              <span className="text-xs font-medium text-gray-600">Current password</span>
              <input
                type="password"
                autoComplete="current-password"
                className="mt-1 w-full rounded-md border border-surface-border px-3 py-2 text-sm focus:border-brand focus:outline-none"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                disabled={busy}
              />
            </label>
            <label className="mt-3 block">
              <span className="text-xs font-medium text-gray-600">New password (10+ characters)</span>
              <input
                type="password"
                autoComplete="new-password"
                className="mt-1 w-full rounded-md border border-surface-border px-3 py-2 text-sm focus:border-brand focus:outline-none"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                disabled={busy}
              />
            </label>
            <label className="mt-3 block">
              <span className="text-xs font-medium text-gray-600">Confirm new password</span>
              <input
                type="password"
                autoComplete="new-password"
                className="mt-1 w-full rounded-md border border-surface-border px-3 py-2 text-sm focus:border-brand focus:outline-none"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={busy}
              />
            </label>
            {next && confirm && next !== confirm && (
              <div className="mt-2 text-xs text-red-600">Passwords don&rsquo;t match.</div>
            )}
            {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
            <div className="mt-4 flex gap-2">
              <button type="button" className="btn-secondary flex-1" onClick={onClose} disabled={busy}>
                Cancel
              </button>
              <button type="submit" className="btn-primary flex-1" disabled={!canSubmit}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
